// Contracts — реестр выгрузок отгрузки («Обзор» → «Текущая отгрузка»).
//
// Кнопка «Скачать zip» собирает архив в браузере, поэтому состав выгрузки
// знает только клиент — он и присылает его сюда сразу после сборки. Без этой
// записи `image_grouping` (номер кадра в отгрузочной таблице) не отобразить
// обратно в asset_id: он перенумеровывается 1..N при каждой сборке.
import { z } from 'zod';

export const SHIPMENT_EXPORT_STATUSES = ['pending', 'confirmed', 'edited', 'superseded'] as const;
export const ShipmentExportStatusSchema = z.enum(SHIPMENT_EXPORT_STATUSES);
export type ShipmentExportStatus = z.infer<typeof ShipmentExportStatusSchema>;

// Снимок строки на момент сборки zip. Денормализован намеренно: он обязан
// совпадать с Metadata_Participant_File.xlsx у заказчика, даже если строка
// каталога после этого изменится.
export const ShipmentExportItemSchema = z.object({
  image_grouping: z.number().int().positive(),
  asset_id: z.string().min(1),
  participant_id: z.string().default(''),
  ship_file_name: z.string().default(''),
  pass_type: z.string().default(''),
  merchant: z.string().default(''),
  country: z.string().default(''),
  locale: z.string().default(''),
  modality: z.string().default(''),
});
export type ShipmentExportItem = z.infer<typeof ShipmentExportItemSchema>;

// POST /api/manager/shipmentExportRecord — фиксация только что скачанного zip.
// Предыдущая активная выгрузка при этом гаснет в 'superseded': актуальна
// всегда последняя скачанная папка.
export const ShipmentExportRecordReqSchema = z.object({
  file_name: z.string().trim().max(200).default(''),
  items: z.array(ShipmentExportItemSchema).min(1).max(20000),
});
export type ShipmentExportRecordReq = z.infer<typeof ShipmentExportRecordReqSchema>;

// Разрез состава для клиентского трекера: по локали × категории. Считается
// на сервере из состава выгрузки, чтобы шаг заполнения трекера не пересчитывал
// то же самое по-своему.
export const ShipmentExportLocaleRowSchema = z.object({
  country: z.string().default(''),
  locale: z.string().default(''),
  total: z.number().int().nonnegative().default(0),
  photos: z.number().int().nonnegative().default(0),
  screenshots: z.number().int().nonnegative().default(0),
  participants: z.number().int().nonnegative().default(0),
});
export type ShipmentExportLocaleRow = z.infer<typeof ShipmentExportLocaleRowSchema>;

export const ShipmentExportSchema = z.object({
  export_id: z.string(),
  batch: z.string().default(''),
  file_name: z.string().default(''),
  status: ShipmentExportStatusSchema,
  total: z.number().int().nonnegative().default(0),
  removed: z.number().int().nonnegative().default(0),
  created_at: z.string().default(''),
  created_by: z.string().default(''),
  confirmed_at: z.string().default(''),
  confirmed_by: z.string().default(''),
  edited_at: z.string().default(''),
  edited_by: z.string().default(''),
  by_locale: z.array(ShipmentExportLocaleRowSchema).default([]),
});
export type ShipmentExport = z.infer<typeof ShipmentExportSchema>;

export const ShipmentExportRecordResSchema = z.object({
  export: ShipmentExportSchema,
});
export type ShipmentExportRecordRes = z.infer<typeof ShipmentExportRecordResSchema>;

// GET /api/manager/shipmentExportLatest — карточка последней выгрузки рядом с
// кнопкой скачивания. `export` = null, если не скачивали ещё ни разу.
export const ShipmentExportLatestResSchema = z.object({
  export: ShipmentExportSchema.nullable().default(null),
});
export type ShipmentExportLatestRes = z.infer<typeof ShipmentExportLatestResSchema>;

// POST /api/manager/shipmentExportConfirm — подтверждение отгрузки заказчику.
// Ассеты состава уходят в 'shipped' с ярлыком партии. `export_id` обязателен:
// подтверждать можно только ту выгрузку, которую менеджер видит на экране,
// иначе параллельная сборка zip в другой вкладке отгрузит не тот состав.
export const ShipmentExportConfirmReqSchema = z.object({
  export_id: z.string().min(1),
});
export type ShipmentExportConfirmReq = z.infer<typeof ShipmentExportConfirmReqSchema>;

export const ShipmentExportConfirmResSchema = z.object({
  export: ShipmentExportSchema,
  shipped: z.number().int().nonnegative().default(0),
});
export type ShipmentExportConfirmRes = z.infer<typeof ShipmentExportConfirmResSchema>;

// POST /api/manager/shipmentExportRemove — выбить кадры из состава по Image
// Grouping (номера из Metadata_Participant_File.xlsx скачанной папки).
// Кадры уходят в 'rejected', выгрузка становится 'edited' и подтверждению
// больше не подлежит: папка у менеджера уже не соответствует базе.
export const ShipmentExportRemoveReqSchema = z.object({
  export_id: z.string().min(1),
  image_groupings: z.array(z.number().int().positive()).min(1).max(5000),
  reason: z.string().trim().max(200).default(''),
});
export type ShipmentExportRemoveReq = z.infer<typeof ShipmentExportRemoveReqSchema>;

export const ShipmentExportRemoveResSchema = z.object({
  export: ShipmentExportSchema,
  removed: z.number().int().nonnegative().default(0),
  // Номера, которых в составе этой выгрузки нет (опечатка в модалке) —
  // показываем менеджеру, а не глотаем молча.
  unknown: z.array(z.number().int()).default([]),
});
export type ShipmentExportRemoveRes = z.infer<typeof ShipmentExportRemoveResSchema>;
